import { Spacer } from '@loomhq/lens';
import { isSupported } from '@loomhq/loom-sdk';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  SHOW_VIDEO_PLAYER_END_SCREEN,
  SIGN_UP_CTA_CLICK,
} from 'constants/events';
import { RECORD_REPLY_SOURCE_VIDEO_END_SCREEN } from 'constants/sdk';
import { watchAgain } from 'creators/videoPlayer';
import * as analytics from 'utilities/analytics';
import { isInstalled } from 'utilities/extension';
import VideoSession from 'utilities/video-session';

import RecordReplyFallbackButton from './record-reply-fallback-button';
import SlackIDLinkGetStartedModal from '../../slack/idlink-get-started-modal';
import {
  GROW_END_OF_VIDEO_REPLY_CTA,
  ROLLOUT_WEBSITE_RECORDING,
} from '../../../../../shared/constants/featureFlag';

import FeatureFlaggedComponent, {
  PrefetchFlags,
} from 'components/feature-flag-wrapper';
import CustomCtaButton from 'components/share-video/cta-button';
import SignupButton from 'components/signup-button';

import './styles.less';

const RecordAReply = ({ onWatchAgainClick }) => {
  return (
    <div className="end-screen-record-video-reply-button">
      <RecordReplyFallbackButton
        source={RECORD_REPLY_SOURCE_VIDEO_END_SCREEN}
      />
      <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
    </div>
  );
};

const GetLoomForFreeCTA = ({
  fromPublicSharePage,
  isInlineEmbedOnLoom,
  onWatchAgainClick,
  videoId,
}) => {
  useEffect(() => {
    analytics.track(SHOW_VIDEO_PLAYER_END_SCREEN, {
      cta_type: 'large-cta',
      video_id: videoId,
      from_url: window.location.href,
    });
  }, [videoId]);

  return (
    <div className="install" data-testid="end-of-video-card">
      <FeatureFlaggedComponent
        flag={GROW_END_OF_VIDEO_REPLY_CTA}
        activationValues={['show-old']}
      >
        <h3 className="static-cta">Get Loom to become faster than typing</h3>
      </FeatureFlaggedComponent>
      <FeatureFlaggedComponent
        flag={GROW_END_OF_VIDEO_REPLY_CTA}
        activationValues={['show-new']}
      >
        <h3 className="static-cta">Reply now with a Loom of your own</h3>
      </FeatureFlaggedComponent>
      <Spacer top="large" />
      <SignupButton
        forceSignupLink
        size="large"
        tracking={{
          eventName: SIGN_UP_CTA_CLICK,
          properties: {
            source: 'End of video',
            from_public_share_page: fromPublicSharePage,
            is_inline_embed_on_loom: isInlineEmbedOnLoom,
            video_id: videoId,
            from_url: window.location.href,
          },
        }}
      />
      <a target="_blank" href="/">
        go to loom.com
      </a>
      <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
    </div>
  );
};

const WatchAgainCta = ({ onWatchAgainClick }) => (
  <div className="watch-again-cta" onClick={onWatchAgainClick}>
    <div className="watch-again-icon" />
    <div>Watch Again</div>
  </div>
);

const EndScreen = props => {
  const {
    recordReplyEnabled,
    ctaEnabled,
    currentTime,
    EditVideoButtons,
    ViewerVideoButtons,
    fromPublicSharePage,
    isInlineEmbedOnLoom,
    isLoggedIn,
    isMainVideoOnSharePage,
    loomBrandedPlayer,
    videoId,
  } = props;

  const dispatch = useDispatch();
  const [hasCheckedIfInstalled, setHasCheckedIfInstalled] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [openOnboardingModal, setOpenOnboardingModal] = useState(false);

  useEffect(() => {
    setHasCheckedIfInstalled(true);

    const checkInstallation = async () => {
      setInstalled(await new Promise(resolve => isInstalled(resolve)));
    };

    checkInstallation();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (!openOnboardingModal) {
        setOpenOnboardingModal(true);
      }
    }, 2000);
  }, [openOnboardingModal]);

  const onWatchAgainClick = ev => {
    ev.stopPropagation();

    dispatch(watchAgain());
  };

  // if logged in and installed, don't upsell
  const showLargeCTA = !installed && !isLoggedIn && loomBrandedPlayer;

  if (!hasCheckedIfInstalled) {
    return null;
  }

  const showRecordAReply =
    isLoggedIn && // Users must be authenticated, will revist: https://linear.app/loom-com/issue/PLFM-431/record-a-reply-for-anonymous-recording
    !ctaEnabled && // Currently, only show the Record a Reply when no CTA is present
    isMainVideoOnSharePage &&
    recordReplyEnabled &&
    isSupported(); // Checks if browser is compatible with loom-sdk

  const NonCTAEndScreen = () => (
    <>
      {showLargeCTA && (
        <GetLoomForFreeCTA
          fromPublicSharePage={fromPublicSharePage}
          isInlineEmbedOnLoom={isInlineEmbedOnLoom}
          onWatchAgainClick={onWatchAgainClick}
          videoId={videoId}
        />
      )}

      <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
    </>
  );

  return (
    <PrefetchFlags flags={[ROLLOUT_WEBSITE_RECORDING]}>
      <div
        className={classNames(
          'video-player-end-screen',
          ctaEnabled && 'has-custom-cta',
          showLargeCTA && 'large-cta'
        )}
      >
        {ctaEnabled && (
          <>
            <CustomCtaButton
              onClick={() => VideoSession.sendEvent('cta', { currentTime })}
            />
            <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
          </>
        )}

        {!ctaEnabled && (
          <div>
            <FeatureFlaggedComponent // If the feature flag is enabled, conditional rendering logic
              flag={ROLLOUT_WEBSITE_RECORDING}
              activationValues={[true]}
            >
              {showRecordAReply ? (
                <RecordAReply onWatchAgainClick={onWatchAgainClick} />
              ) : (
                <NonCTAEndScreen />
              )}
            </FeatureFlaggedComponent>
            <FeatureFlaggedComponent // Otherwise, just render the NonCTAEndScreen
              flag={ROLLOUT_WEBSITE_RECORDING}
              activationValues={[false]}
            >
              <NonCTAEndScreen />
            </FeatureFlaggedComponent>
          </div>
        )}

        {EditVideoButtons && (
          <div className="edit-video-buttons absolute right:0 text:right">
            <EditVideoButtons startCollapsed />
          </div>
        )}
        {ViewerVideoButtons && (
          <div
            className={`${
              ctaEnabled
                ? 'viewer-video-buttons-with-cta'
                : 'viewer-video-buttons'
            } absolute right:0 text:right`}
          >
            <ViewerVideoButtons startCollapsed />
          </div>
        )}
        <SlackIDLinkGetStartedModal
          redirectOnClick
          openByDefault={openOnboardingModal}
          variant="videoSharePage"
        />
      </div>
    </PrefetchFlags>
  );
};

export default EndScreen;
